import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AddressAutocomplete } from '@/components/common/AddressAutocomplete';
import { advanceOnEnter } from '@/utils/keyboard';
import { AccessoryPicker } from '@/components/common/AccessoryPicker';
import { ReferredByAutocomplete } from '@/components/common/ReferredByAutocomplete';
import { API } from '@/utils/constants';
import { HelpTooltip } from '@/components/HelpTooltip';
import { printJobSticker } from '@/utils/sticker';
import { todayLocalISO } from '@/utils/date';

/**
 * New Job Form Component
 * Create new jobs with customer lookup and form validation
 */
const NewJobForm = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    company_name: '',
    abn: '',
    customer_reference: '',
    address_line1: '',
    suburb: '',
    postcode: '',
    state: '',
    email: '',
    mobile: '',
    customer_type: '',
    newsletter_consent: false,
    status: '',
    job_type: '',
    brand: '',
    model_number: '',
    serial_number: '',
    accessories: [],
    job_description: '',
    date_in: todayLocalISO(),
    date_out: '',
    date_due: '',
    date_finished: '',
    total_amount: 0,
    gst_percentage: 10,
    gst_amount: 0,
    subtotal: 0,
    discount_percent: 0,
    discount_amount: 0,
    rrp_total_amount: 0,
    referral_source: '',
    referred_by_partner_id: null,
    referred_by_partner_name: '',
    priority: 'Standard',
    closed: false,
    initial_comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Edit-before-send dialog for the signature request email
  const [showSignatureEmailDialog, setShowSignatureEmailDialog] = useState(false);
  const [alsoPrintSticker, setAlsoPrintSticker] = useState(true);  // default: print sticker as part of the email-signature workflow
  const [sigEmailSubject, setSigEmailSubject] = useState('');
  const [sigEmailBody, setSigEmailBody] = useState('');
  const [sigEmailCc, setSigEmailCc] = useState('');
  const [sigEmailBcc, setSigEmailBcc] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      // Set default status if available
      if (response.data.statuses.length > 0) {
        setFormData(prev => ({ ...prev, status: response.data.statuses[0] }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const searchCustomers = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/customers?search=${searchText}`);
      setCustomerSuggestions(response.data);
      setShowSuggestions(response.data.length > 0);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    searchCustomers(value);
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_name: customer.name,
      // Feb 2026 — Business-customer fields pull through from the picked
      // customer so repeat business bookings don't need to re-type them.
      company_name: customer.company_name || '',
      abn: customer.abn || '',
      mobile: customer.mobile,
      email: customer.email,
      address_line1: customer.address_line1,
      suburb: customer.suburb,
      postcode: customer.postcode,
      state: customer.state,
      customer_type: customer.customer_type || '',
      // Pre-tick the newsletter checkbox for returning customers who've already
      // consented so the state reflects reality. Unsubscribed customers stay
      // unticked (respects prior opt-out).
      newsletter_consent: !!customer.newsletter_consent && !customer.newsletter_unsubscribed,
    });
    setCustomerSearch(customer.name);
    setShowSuggestions(false);
    toast.success('Customer details loaded');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.job_type) {
      toast.error('Please select a Job Type');
      return;
    }
    if (!formData.brand) {
      toast.error('Please select a Brand');
      return;
    }
    
    setSubmitting(true);

    try {
      // Clean up form data - convert empty strings to null for optional date fields,
      // and apply partner discount if any (RRP → net) via buildSubmitPayload.
      const submitData = buildSubmitPayload();
      
      const response = await axios.post(`${API}/jobs`, submitData);
      toast.success(`Job #${response.data.job_number} created successfully!`);
      navigate('/');
    } catch (error) {
      console.error('Error creating job:', error);
      let errorMessage = 'Failed to create job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndPrintSticker = async () => {
    // Saves the job, then immediately fires the printSticker flow using the
    // newly-issued job_number. Workflow: user finishes intake → one click →
    // job saved and sticker on the printer, no need to reopen the job.
    if (!formData.job_type) { toast.error('Please select a Job Type'); return; }
    if (!formData.brand) { toast.error('Please select a Brand'); return; }

    setSubmitting(true);
    try {
      const submitData = buildSubmitPayload();
      const response = await axios.post(`${API}/jobs`, submitData);
      const newJob = response.data;
      toast.success(`Job #${newJob.job_number} created — sending to printer…`);

      // Fire the sticker print (settings already in component state)
      try {
        printJobSticker(
          {
            job_number: newJob.job_number,
            customer_name: newJob.customer_name || formData.customer_name,
            mobile: newJob.mobile || formData.mobile,
          },
          settings,
        );
      } catch (printErr) {
        console.error('Sticker print error:', printErr);
        toast.error('Job saved but sticker print failed — open the job and reprint');
      }

      navigate('/');
    } catch (error) {
      console.error('Error creating job:', error);
      let errorMessage = 'Failed to create job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAndSendSignatureLink = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.job_type) {
      toast.error('Please select a Job Type');
      return;
    }
    if (!formData.brand) {
      toast.error('Please select a Brand');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter customer email');
      return;
    }
    
    // Pre-fill the dialog with the saved template (with placeholders left intact for preview)
    const tpl = settings?.email_templates?.job_booked_signature || {};
    const subject = tpl.subject || 'Please Sign Your Job Card - Job #{job_number}';
    const body = tpl.body || `Dear ${formData.customer_name || '{customer_name}'},\n\nPlease review and sign your job card by clicking the button below.\n\nThank you.`;
    
    // Resolve customer_name placeholder upfront for a friendlier preview (others resolved server-side)
    const resolvedBody = body.replace(/\{customer_name\}/g, formData.customer_name || '{customer_name}');
    
    setSigEmailSubject(subject);
    setSigEmailBody(resolvedBody);
    setSigEmailCc(tpl.default_cc || '');
    setSigEmailBcc(tpl.default_bcc || '');
    setShowSignatureEmailDialog(true);
  };

  const confirmCreateAndSendSignatureLink = async () => {
    setShowSignatureEmailDialog(false);
    setSubmitting(true);
    
    try {
      // Clean up form data - convert empty strings to null for optional fields
      // Start from buildSubmitPayload so the partner discount (RRP → net) is applied,
      // then layer on the signature-flow-specific overrides + belt-and-braces
      // string cleanup for text fields.
      const submitData = {
        ...buildSubmitPayload(),
        // Ensure date_in has a value (should be set on form load)
        date_in: formData.date_in || todayLocalISO(),
        // Convert empty date strings to null (MUST check for empty string explicitly)
        date_out: formData.date_out && formData.date_out.trim() !== '' ? formData.date_out : null,
        date_due: formData.date_due && formData.date_due.trim() !== '' ? formData.date_due : null,
        date_finished: formData.date_finished && formData.date_finished.trim() !== '' ? formData.date_finished : null,
        // Clean up optional text fields (empty strings are OK for text)
        model_number: formData.model_number || "",
        serial_number: formData.serial_number || "",
        accessories: Array.isArray(formData.accessories) ? formData.accessories : [],
        job_description: formData.job_description || "",
        initial_comment: formData.initial_comment || "",
        // Edit-before-send overrides (server-side will fall back to template if any field is missing)
        email_subject: sigEmailSubject,
        email_body: sigEmailBody,
        email_cc: sigEmailCc,
        email_bcc: sigEmailBcc,
      };
      
      const response = await axios.post(`${API}/jobs/create-and-send-signature-link`, submitData);
      toast.success(response.data.message || 'Job created and signature link sent!');

      // If the user opted to also print the sticker (checkbox in the email dialog, default ON),
      // fire the sticker print now using the newly-created job data returned by the API.
      // This is the "one-click Save + Print + Email" flow — no need to reopen the job.
      if (alsoPrintSticker) {
        try {
          const newJob = response.data.job || {};
          printJobSticker(
            {
              job_number: newJob.job_number,
              customer_name: newJob.customer_name || formData.customer_name,
              mobile: newJob.mobile || formData.mobile,
            },
            settings,
          );
        } catch (printErr) {
          console.error('Sticker print error:', printErr);
          toast.error('Job saved & emailed, but sticker print failed — open the job and reprint');
        }
      }

      navigate('/');
    } catch (error) {
      console.error('Error creating job and sending signature link:', error);
      console.error('Error response:', error.response?.data);
      console.error('Full error details:', JSON.stringify(error.response?.data, null, 2));
      
      let errorMessage = 'Failed to create job or send signature link';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors - show each field error
          const fieldErrors = detail.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : 'unknown';
            const msg = err.msg || 'validation error';
            const input = err.input !== undefined ? ` (value: ${JSON.stringify(err.input)})` : '';
            return `${field}: ${msg}${input}`;
          }).join('\n');
          errorMessage = `Validation errors:\n${fieldErrors}`;
          
          // Also show in console with full details
          console.error('VALIDATION ERRORS:', detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error with longer duration
      toast.error(errorMessage, { duration: 10000 });
      
      // Alert with full details for debugging
      alert(`ERROR DETAILS:\n\n${errorMessage}\n\nCheck console for more info.`);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateFinancials = (rrpTotal, gstPercentage, discountPercent = 0) => {
    // The user types the RRP (inc GST). Discount % is applied to this.
    // Everything downstream (subtotal, GST, total) reflects the NET (post-discount) amount,
    // so all existing revenue/payment reports keep working unchanged.
    const rrp = parseFloat(rrpTotal) || 0;
    const gstPercent = parseFloat(gstPercentage) || 10;
    const discPercent = Math.max(0, Math.min(100, parseFloat(discountPercent) || 0));
    const discountAmt = parseFloat((rrp * (discPercent / 100)).toFixed(2));
    const netTotal = parseFloat((rrp - discountAmt).toFixed(2));
    const subtotal = netTotal / (1 + gstPercent / 100);
    const gstAmount = netTotal - subtotal;
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      gst_amount: parseFloat(gstAmount.toFixed(2)),
      discount_amount: discountAmt,
      net_total: netTotal,
    };
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Recalculate whenever total, GST%, or discount% changes.
      // `total_amount` is treated as the RRP the user typed; we recompute net below.
      if (field === 'total_amount' || field === 'gst_percentage' || field === 'discount_percent') {
        const rrp = field === 'total_amount' ? value : prev.total_amount;
        const gstPct = field === 'gst_percentage' ? value : prev.gst_percentage;
        const discPct = field === 'discount_percent' ? value : prev.discount_percent;
        const { subtotal, gst_amount, discount_amount } = calculateFinancials(rrp, gstPct, discPct);
        newData.subtotal = subtotal;
        newData.gst_amount = gst_amount;
        newData.discount_amount = discount_amount;
      }
      return newData;
    });
  };

  /**
   * Convert the on-screen formData into the payload the backend expects.
   * The user types the RRP in the "Total (inc GST)" field; if a Partner Discount %
   * is set, the actual `total_amount` we persist is the NET (post-discount) so all
   * existing revenue/payment logic stays untouched. rrp_total_amount preserves the
   * original figure for audit + invoice line-item display.
   */
  const buildSubmitPayload = () => {
    const rrp = parseFloat(formData.total_amount) || 0;
    const discPct = Math.max(0, Math.min(100, parseFloat(formData.discount_percent) || 0));
    const discAmt = parseFloat((rrp * (discPct / 100)).toFixed(2));
    const netTotal = parseFloat((rrp - discAmt).toFixed(2));
    return {
      ...formData,
      date_out: formData.date_out || null,
      date_due: formData.date_due || null,
      date_finished: formData.date_finished || null,
      total_amount: netTotal,          // what the customer actually owes
      rrp_total_amount: rrp,           // pre-discount reference
      discount_percent: discPct,
      discount_amount: discAmt,
    };
  };

  if (!settings) {
    return <div className="loading">Loading form...</div>;
  }

  return (
    <div className="form-container">
      <Card className="job-form-card">
        <CardHeader>
          <CardTitle>New Job Booking</CardTitle>
          <CardDescription>Fill in the details to create a new job</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} onKeyDown={advanceOnEnter} className="job-form" data-testid="new-job-form">
            {/* Customer Search/Lookup */}
            <div className="form-section" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '1rem' }}>Search Existing Customer</h3>
              <div style={{ position: 'relative' }}>
                <Input
                  type="text"
                  placeholder="Start typing customer name, mobile, or email..."
                  value={customerSearch}
                  onChange={handleCustomerSearchChange}
                  onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
                  style={{ width: '100%' }}
                />
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {customerSuggestions.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: '600' }}>{customer.name}</div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>
                          {customer.mobile} • {customer.email}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>
                          {customer.suburb}, {customer.state} {customer.postcode}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                Select an existing customer to auto-fill their details, or enter new customer information below.
              </p>
            </div>

            <div className="form-section">
              <h3>
                Customer Information
                <HelpTooltip 
                  content="Start typing a customer name to see existing matches. Select a customer to auto-fill their details, or enter new info to create a new customer." 
                  side="right"
                />
              </h3>
              <div className="form-grid">
                <div className="form-field">
                  <Label htmlFor="customer_name">{settings.field_names.customer_name}</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    required
                    data-testid="customer-name-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Business customer's company (optional)"
                    data-testid="company-name-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="mobile">{settings.field_names.mobile}</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    required
                    data-testid="mobile-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="customer_reference">Customer Reference</Label>
                  <Input
                    id="customer_reference"
                    value={formData.customer_reference}
                    onChange={(e) => handleChange('customer_reference', e.target.value)}
                    placeholder="Customer's own reference number"
                    data-testid="customer-reference-input"
                  />
                </div>

                <div className="form-field" style={{ gridColumn: 2 }}>
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    value={formData.abn}
                    onChange={(e) => handleChange('abn', e.target.value)}
                    placeholder="Australian Business Number (optional)"
                    data-testid="abn-input"
                  />
                </div>

                <div className="form-field" style={{ gridColumn: 2 }}>
                  <Label htmlFor="referred_by_partner_name">
                    Referred By
                    <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>
                      (partner who sent this customer)
                    </span>
                  </Label>
                  <ReferredByAutocomplete
                    value={{ id: formData.referred_by_partner_id, name: formData.referred_by_partner_name }}
                    onChange={({ id, name }) => {
                      // Auto-set the coarse channel to "Partner" when a specific referrer is chosen.
                      // Still user-editable via the dropdown below — this just saves a click for the common case.
                      setFormData((prev) => ({
                        ...prev,
                        referred_by_partner_id: id || null,
                        referred_by_partner_name: name || '',
                        referral_source: id ? 'Partner' : prev.referral_source,
                      }));
                    }}
                    placeholder="Start typing e.g. DRC…"
                    testId="referred-by-input"
                  />
                </div>

                <div className="form-field full-width">
                  <Label htmlFor="email">{settings.field_names.email} <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.75rem' }}>(optional)</span></Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    data-testid="email-input"
                  />
                </div>

                <div className="form-field full-width">
                  <Label htmlFor="address_line1">{settings.field_names.address_line1 || 'Address Line 1'}</Label>
                  <AddressAutocomplete
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(value) => handleChange('address_line1', value)}
                    onAddressSelect={(addressData) => {
                      // Auto-fill all address fields when an address is selected
                      if (addressData.address_line1) handleChange('address_line1', addressData.address_line1);
                      if (addressData.suburb) handleChange('suburb', addressData.suburb);
                      if (addressData.postcode) handleChange('postcode', addressData.postcode);
                      if (addressData.state) handleChange('state', addressData.state);
                      toast.success('Address auto-filled!');
                    }}
                    placeholder="Start typing address..."
                    data-testid="address-line1-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="suburb">{settings.field_names.suburb || 'Suburb'}</Label>
                  <Input
                    id="suburb"
                    value={formData.suburb}
                    onChange={(e) => handleChange('suburb', e.target.value)}
                    required
                    data-testid="suburb-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="postcode">{settings.field_names.postcode || 'Postcode'}</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => handleChange('postcode', e.target.value)}
                    required
                    data-testid="postcode-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="state">{settings.field_names.state || 'State'}</Label>
                  <Select value={formData.state} onValueChange={(value) => handleChange('state', value)} required>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.states || ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT']).map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>
                Job Details
                <HelpTooltip 
                  content="Set the initial status (usually 'Booked'), priority level, job type, and dates. The job number is auto-generated based on your settings." 
                  side="right"
                />
              </h3>
              <div className="form-grid">
                <div className="form-field">
                  <Label htmlFor="status">{settings.field_names.status}</Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange('status', value)} required>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {settings.statuses.map((status) => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-field">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.priorities || ['Standard', 'Urgent', 'Special']).map((priority) => (
                        <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-field">
                  <Label htmlFor="job_type">{settings.field_names.job_type}</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                    <Input
                      id="job_type"
                      list="job-type-suggestions"
                      value={formData.job_type}
                      onChange={(e) => handleChange('job_type', e.target.value)}
                      placeholder="Start typing a job type"
                      data-testid="job-type-select"
                      autoComplete="off"
                      required
                      style={{ flex: 1 }}
                    />
                    <datalist id="job-type-suggestions">
                      {(settings.job_types || []).map((t) => <option key={t} value={t} />)}
                    </datalist>
                    {formData.job_type && !(settings.job_types || []).some(t => t.toLowerCase() === formData.job_type.toLowerCase()) && (
                      <Button
                        type="button"
                        data-testid="job-type-add-btn"
                        onClick={async () => {
                          const val = formData.job_type.trim();
                          if (!val) return;
                          if ((settings.job_types || []).some(t => t.toLowerCase() === val.toLowerCase())) return;
                          try {
                            const next = [...(settings.job_types || []), val].sort((a, b) => a.localeCompare(b));
                            await axios.put(`${API}/settings`, { ...settings, job_types: next });
                            setSettings({ ...settings, job_types: next });
                            toast.success(`Added "${val}" to job types`);
                          } catch (err) {
                            toast.error(`Couldn't save: ${err?.response?.data?.detail || err.message}`);
                          }
                        }}
                        style={{ background: '#16a34a', color: '#fff', whiteSpace: 'nowrap' }}
                        title={`Save "${formData.job_type}" to your job type list for future jobs`}
                      >
                        + Add {formData.job_type.length > 10 ? formData.job_type.slice(0, 10) + '…' : formData.job_type}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="brand">{settings.field_names.brand}</Label>
                  {/* Feb 2026 — replaced native Select with datalist-backed
                      autocomplete + inline "Add {brand}" button. As the brand
                      list grew scrolling became tedious. User types the first
                      few letters, browser filters, and if the brand isn't in
                      the list yet a green "+ Add" button appears — one click
                      persists it to settings.brands and pre-selects it.
                      Mirrors the supplier/stock inline-add pattern. */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                    <Input
                      id="brand"
                      list="brand-suggestions"
                      value={formData.brand}
                      onChange={(e) => handleChange('brand', e.target.value)}
                      placeholder="Start typing a brand (e.g. Maxtor)"
                      data-testid="brand-select"
                      autoComplete="off"
                      required
                      style={{ flex: 1 }}
                    />
                    <datalist id="brand-suggestions">
                      {(settings.brands || []).map((b) => (
                        <option key={b} value={b} />
                      ))}
                    </datalist>
                    {formData.brand && !(settings.brands || []).some(b => b.toLowerCase() === formData.brand.toLowerCase()) && (
                      <Button
                        type="button"
                        data-testid="brand-add-btn"
                        onClick={async () => {
                          const newBrand = formData.brand.trim();
                          if (!newBrand) return;
                          if ((settings.brands || []).some(b => b.toLowerCase() === newBrand.toLowerCase())) return;
                          try {
                            const nextBrands = [...(settings.brands || []), newBrand].sort((a, b) => a.localeCompare(b));
                            await axios.put(`${API}/settings`, { ...settings, brands: nextBrands });
                            setSettings({ ...settings, brands: nextBrands });
                            toast.success(`Added "${newBrand}" to brands`);
                          } catch (err) {
                            toast.error(`Couldn't save brand: ${err?.response?.data?.detail || err.message}`);
                          }
                        }}
                        style={{ background: '#16a34a', color: '#fff', whiteSpace: 'nowrap' }}
                        title={`Save "${formData.brand}" to your brand list for future jobs`}
                      >
                        + Add {formData.brand.length > 10 ? formData.brand.slice(0, 10) + '…' : formData.brand}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="referral_source">How did you find us?</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                    <Input
                      id="referral_source"
                      list="referral-source-suggestions"
                      value={formData.referral_source}
                      onChange={(e) => handleChange('referral_source', e.target.value)}
                      placeholder="Start typing a source"
                      data-testid="referral-source-select"
                      autoComplete="off"
                      style={{ flex: 1 }}
                    />
                    <datalist id="referral-source-suggestions">
                      {(settings.referral_sources || ['Google', 'Referral', 'Partner', 'Walk-in']).map((s) => <option key={s} value={s} />)}
                    </datalist>
                    {formData.referral_source && !(settings.referral_sources || []).some(s => s.toLowerCase() === formData.referral_source.toLowerCase()) && (
                      <Button
                        type="button"
                        data-testid="referral-source-add-btn"
                        onClick={async () => {
                          const val = formData.referral_source.trim();
                          if (!val) return;
                          if ((settings.referral_sources || []).some(s => s.toLowerCase() === val.toLowerCase())) return;
                          try {
                            const next = [...(settings.referral_sources || []), val].sort((a, b) => a.localeCompare(b));
                            await axios.put(`${API}/settings`, { ...settings, referral_sources: next });
                            setSettings({ ...settings, referral_sources: next });
                            toast.success(`Added "${val}" to referral sources`);
                          } catch (err) {
                            toast.error(`Couldn't save: ${err?.response?.data?.detail || err.message}`);
                          }
                        }}
                        style={{ background: '#16a34a', color: '#fff', whiteSpace: 'nowrap' }}
                        title={`Save "${formData.referral_source}" to your referral source list for future jobs`}
                      >
                        + Add {formData.referral_source.length > 10 ? formData.referral_source.slice(0, 10) + '…' : formData.referral_source}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="customer_type">Customer Type (for Newsletter)</Label>
                  <Select value={formData.customer_type} onValueChange={(value) => handleChange('customer_type', value)}>
                    <SelectTrigger data-testid="customer-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.customer_types || ['End User', 'Partner']).map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Newsletter opt-in checkbox — captured at intake per Spam Act 2003.
                      Ticking upgrades the customer's newsletter_consent to true with
                      source='job_form'. Unticking a repeat booking DOES NOT revoke an
                      existing consent (server logic guards against silent opt-out). */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!formData.newsletter_consent}
                      onChange={(e) => handleChange('newsletter_consent', e.target.checked)}
                      data-testid="newsletter-consent-checkbox"
                    />
                    <span>📮 Add customer to newsletter list <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>(they said yes when asked)</span></span>
                  </label>
                </div>

                <div className="form-field">
                  <Label htmlFor="model_number">{settings.field_names.model_number}</Label>
                  <Input
                    id="model_number"
                    value={formData.model_number}
                    onChange={(e) => handleChange('model_number', e.target.value)}
                    data-testid="model-number-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="serial_number">{settings.field_names.serial_number}</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => handleChange('serial_number', e.target.value)}
                    data-testid="serial-number-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="accessories">{settings.field_names.accessories}</Label>
                  <AccessoryPicker
                    value={formData.accessories}
                    onChange={(list) => handleChange('accessories', list)}
                    options={settings.accessory_options || []}
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="date_in">{settings.field_names.date_in}</Label>
                  <Input
                    id="date_in"
                    type="date"
                    value={formData.date_in}
                    onChange={(e) => handleChange('date_in', e.target.value)}
                    required
                    data-testid="date-in-input"
                  />
                </div>

                {/* Date Out, Date Finished, and Date Due are intentionally hidden during
                    initial booking-in. They are filled in later from the Job Detail page
                    as the job progresses (Date Due once the customer approves the quote,
                    Date Finished when work is complete, Date Out when the device is collected). */}

                <div className="form-field full-width">
                  <Label htmlFor="job_description">{settings.field_names.job_description}</Label>
                  <Textarea
                    id="job_description"
                    value={formData.job_description}
                    onChange={(e) => handleChange('job_description', e.target.value)}
                    rows={3}
                    data-testid="job-description-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Financial Information</h3>
              <div className="form-grid">
                <div className="form-field">
                  <Label htmlFor="total_amount">
                    Total Amount (inc. GST)
                    {parseFloat(formData.discount_percent) > 0 && (
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>
                        (RRP — pre-discount)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="total_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_amount}
                    onChange={(e) => handleChange('total_amount', e.target.value)}
                    placeholder="0.00"
                    data-testid="total-amount-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="discount_percent">
                    Partner Discount %
                    <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>
                      (optional)
                    </span>
                  </Label>
                  <Input
                    id="discount_percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => handleChange('discount_percent', e.target.value)}
                    placeholder="0"
                    data-testid="discount-percent-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="gst_percentage">GST %</Label>
                  <Input
                    id="gst_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.gst_percentage}
                    onChange={(e) => handleChange('gst_percentage', e.target.value)}
                    data-testid="gst-percentage-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="subtotal">Subtotal (ex. GST)</Label>
                  <Input
                    id="subtotal"
                    type="number"
                    value={formData.subtotal}
                    readOnly
                    disabled
                    className="bg-gray-100"
                    data-testid="subtotal-input"
                  />
                </div>

                <div className="form-field">
                  <Label htmlFor="gst_amount">GST Amount</Label>
                  <Input
                    id="gst_amount"
                    type="number"
                    value={formData.gst_amount}
                    readOnly
                    disabled
                    className="bg-gray-100"
                    data-testid="gst-amount-input"
                  />
                </div>

                <div className="form-field full-width">
                  <Label htmlFor="initial_comment">Initial Comment (Optional)</Label>
                  <Textarea
                    id="initial_comment"
                    value={formData.initial_comment}
                    onChange={(e) => handleChange('initial_comment', e.target.value)}
                    rows={3}
                    placeholder="Add an initial comment for this job..."
                    data-testid="initial-comment-input"
                  />
                </div>

                {/* Discount breakdown — only visible when a Partner Discount % is entered.
                    Shows RRP → discount → Net so the user (and later, the printed invoice)
                    can see the numbers clearly before saving. */}
                {parseFloat(formData.discount_percent) > 0 && parseFloat(formData.total_amount) > 0 && (
                  <div
                    className="form-field full-width"
                    data-testid="discount-breakdown"
                    style={{
                      padding: '0.85rem 1rem',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                      <span style={{ color: '#1e3a8a' }}>RRP Total (inc. GST):</span>
                      <span style={{ fontFamily: 'monospace' }}>${(parseFloat(formData.total_amount) || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: '#b91c1c' }}>
                      <span>Partner Discount ({(parseFloat(formData.discount_percent) || 0).toFixed(2)}%):</span>
                      <span style={{ fontFamily: 'monospace' }}>
                        -${(parseFloat(formData.total_amount) * (parseFloat(formData.discount_percent) / 100) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '0.4rem 0 0.15rem', marginTop: '0.3rem',
                        borderTop: '1px solid #bfdbfe',
                        fontWeight: 700, color: '#065f46',
                      }}
                    >
                      <span>Net Total (inc. GST):</span>
                      <span style={{ fontFamily: 'monospace' }}>
                        ${(parseFloat(formData.total_amount) * (1 - parseFloat(formData.discount_percent) / 100) || 0).toFixed(2)}
                      </span>
                    </div>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#475569' }}>
                      Invoice will show the discount as its own line. Revenue reports use the Net Total.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => { if (window.history.length > 1) { navigate(-1); } else { navigate('/'); } }} data-testid="cancel-button">
                Cancel
              </Button>
              <Button
                type="button"
                disabled={submitting}
                variant="outline"
                onClick={handleSaveAndPrintSticker}
                data-testid="save-and-print-sticker-button"
                title="Save the job and immediately print its sticker"
              >
                {submitting ? 'Saving…' : '🏷️ Save & Print Sticker'}
              </Button>
              <Button type="submit" disabled={submitting} variant="outline" data-testid="submit-job-button">
                {submitting ? 'Saving...' : 'Save Job Only'}
              </Button>
              <Button 
                type="button" 
                disabled={submitting} 
                onClick={handleCreateAndSendSignatureLink}
                style={{ backgroundColor: '#10b981', color: 'white' }}
                data-testid="save-and-send-button"
              >
                {submitting ? 'Saving & Sending...' : '📧 Save & Send Signature Link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Signature Request Email Dialog — edit before send (Subject/Body/CC/BCC) */}
      <Dialog open={showSignatureEmailDialog} onOpenChange={setShowSignatureEmailDialog}>
        <DialogContent style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Send Signature Request Email</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div style={{ padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
              <strong>Customer:</strong> {formData.customer_name || '—'}
              <br/><span style={{ color: '#475569' }}>To: <strong>{formData.email || '—'}</strong></span>
              <br/><span style={{ color: '#475569' }}>The &quot;Review &amp; Sign Job Card&quot; button and job details box will be appended automatically below your message. Job number is filled in after creation.</span>
            </div>
            <div>
              <Label htmlFor="sig-email-subject">Subject</Label>
              <Input
                id="sig-email-subject"
                value={sigEmailSubject}
                onChange={(e) => setSigEmailSubject(e.target.value)}
                data-testid="sig-email-subject"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <Label htmlFor="sig-email-cc">CC (comma-separated)</Label>
                <Input id="sig-email-cc" value={sigEmailCc} onChange={(e) => setSigEmailCc(e.target.value)} data-testid="sig-email-cc" />
              </div>
              <div>
                <Label htmlFor="sig-email-bcc">BCC (comma-separated)</Label>
                <Input id="sig-email-bcc" value={sigEmailBcc} onChange={(e) => setSigEmailBcc(e.target.value)} data-testid="sig-email-bcc" />
              </div>
            </div>
            <div>
              <Label htmlFor="sig-email-body">Message</Label>
              <Textarea
                id="sig-email-body"
                rows={12}
                value={sigEmailBody}
                onChange={(e) => setSigEmailBody(e.target.value)}
                data-testid="sig-email-body"
                style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
              />
              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                Edit the default in Settings → Email Templates → Job Booked - Signature Request Email.
              </p>
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 0.75rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              <input
                type="checkbox"
                data-testid="sig-also-print-sticker"
                checked={alsoPrintSticker}
                onChange={(e) => setAlsoPrintSticker(e.target.checked)}
              />
              <span><strong>🏷️ Also print the job sticker now</strong> — saves you reopening the job after sending the signature email.</span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => setShowSignatureEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={confirmCreateAndSendSignatureLink}
              disabled={submitting || !sigEmailSubject.trim() || !sigEmailBody.trim()}
              style={{ backgroundColor: '#10b981', color: 'white' }}
              data-testid="sig-email-send-btn"
            >
              📧 Create Job &amp; Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Job Detail/Edit Component

export default NewJobForm;
